import { LOGIN_LOCAL_FOR_SCREEN } from '@app/consts/common_consts';
import { VITE_ENV } from '@app/consts/env_consts';
import { CommonErrorType, HttpStatusCode } from '@app/modules/error-handler/ErrorHandleModel';
import httpClient, { apsHttpClient } from '@app/modules/http/HttpClient';
import { updateAuthorizationHeader } from '@app/modules/interceptors/AuthInterceptor';
import { NAVIGATE_ROUTE } from '@app/modules/routes/common-route';
import { Endpoints } from '@settings/endpoints-setting';
import axios, { AxiosHeaders, AxiosRequestConfig } from 'axios';
import { t } from 'i18next';
import { AccessToken, BucketObject, TranslationStatus } from '../types';
import { AUTH_ENDPOINTS } from './endpointUrl';

const API_ENDPOINT = Endpoints.COMMON.API_ENDPOINT;
const API_TIMEOUT = Endpoints.TIMEOUT;
const CRITICAL_STATUS_CODES = [
  HttpStatusCode.Unauthorized,
  HttpStatusCode.Forbidden,
  HttpStatusCode.NotFound,
  HttpStatusCode.RequestTimeout,
  HttpStatusCode.InternalServerError,
  HttpStatusCode.ServiceUnavailable,
] as const;

const isCriticalStatusCode = (statusCode: number): boolean => {
  return CRITICAL_STATUS_CODES.includes(statusCode as (typeof CRITICAL_STATUS_CODES)[number]);
};

const activeRequestControllers = new Set<AbortController>();

const cancelAllPendingRequests = () => {
  activeRequestControllers.forEach((controller) => controller.abort());
  activeRequestControllers.clear();
};

const isRequestCanceledError = (error: unknown): boolean => {
  return axios.isCancel(error) || (axios.isAxiosError(error) && error.code === 'ERR_CANCELED');
};

/**
 * Custom error class for handled redirects (e.g. 401 already redirected to SSO)
 * Component should NOT show toast for this error
 */
export class HandledRedirectError extends Error {
  constructor(
    statusCode: number,
    public readonly messageCode = ''
  ) {
    super(`Redirect already handled for status: ${statusCode}`);
    this.name = 'HandledRedirectError';
    Object.assign(this, { statusCode });
  }
}

/**
 * File Storage Record interface
 */
export interface FileStorageRecord {
  fileName: string;
  urn: string;
  path: string;
  version: number;
  uploadedAt: string;
  fileSize: number;
  projectId: string;
  planId: string;
  projectName: string;
  ifcSchema: string;
  globalId: string;
  ifcFileType: string;
}

/**
 * API Error Response interface
 */
export interface ApiErrorResponse {
  field?: string;
  code: string;
  messageJp: string;
  messageEn: string;
  statusCode: number;
}

/**
 * Standard API Response interface
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  field?: string;
  code?: string;
  messageJp?: string;
  messageEn?: string;
}

/** Api error mapping interface */
export interface ApiErrorMapping {
  httpStatus: number;
  success: boolean;
  errorCode?: string;
  errorMessage?: string;
  errorDetails?: any;
  placeholders?: any;
  field?: string;
  code?: string;
  messageJp?: string;
  messageEn?: string;
}

/**
 * Error message mapping
 */
const ERROR_MESSAGE_MAP: Record<number, { messageIdJP: string; messageIdEN: string }> = {
  [HttpStatusCode.InternalServerError]: { messageIdJP: 'MSG_ERR_COM_00901', messageIdEN: 'MSG_ERR_COM_00901' },
  [HttpStatusCode.Unauthorized]: { messageIdJP: 'MSG_ERR_COM_00902', messageIdEN: 'MSG_ERR_COM_00902' },
  [HttpStatusCode.Forbidden]: { messageIdJP: 'MSG_ERR_COM_00903', messageIdEN: 'MSG_ERR_COM_00903' },
  [HttpStatusCode.NotFound]: { messageIdJP: 'MSG_ERR_COM_00904', messageIdEN: 'MSG_ERR_COM_00904' },
  [HttpStatusCode.RequestTimeout]: { messageIdJP: 'MSG_ERR_COM_00905', messageIdEN: 'MSG_ERR_COM_00905' },
  [HttpStatusCode.Conflict]: { messageIdJP: 'MSG_ERR_COM_00906', messageIdEN: 'MSG_ERR_COM_00906' },
  [HttpStatusCode.ServiceUnavailable]: { messageIdJP: 'MSG_ERR_COM_00907', messageIdEN: 'MSG_ERR_COM_00907' },
};

/**
 * Get appropriate error message based on status code
 */
function getErrorMessage(statusCode: number, responseData?: any): ApiErrorResponse {
  const messageIds = ERROR_MESSAGE_MAP[statusCode];

  if (messageIds) {
    return {
      code: messageIds.messageIdJP,
      messageJp: messageIds.messageIdJP,
      messageEn: messageIds.messageIdEN,
      statusCode,
    };
  }

  // For 400 Bad Request, use error from response
  if (statusCode === HttpStatusCode.BadRequest) {
    return {
      code: responseData?.code || 'VALIDATION_ERROR',
      messageJp: responseData?.messageJp || 'リクエストが無効です。',
      messageEn: responseData?.messageEn || 'The request is invalid.',
      statusCode,
    };
  }

  // Default error
  return {
    code: 'UNKNOWN_ERROR',
    messageJp: 'エラーが発生しました。',
    messageEn: 'An error has occurred.',
    statusCode,
  };
}

/**
 * Navigation callback for error handling
 */
let navigationCallback: ((path: string, state?: any) => void) | null = null;

/**
 * Flag to prevent duplicated handling when multiple parallel requests fail with critical status codes
 */
let isCriticalHandlingInProgress = false;

/**
 * Set navigation callback for error handling
 */
export function setNavigationCallback(callback: (path: string, state?: any) => void) {
  navigationCallback = callback;
}

/**
 * Reset critical handling flag (called when navigating back to login)
 */
export function reset401Flag() {
  isCriticalHandlingInProgress = false;
}

/**
 * Endpoints that should NOT trigger navigation redirect on error.
 * E.g. logout — session cleanup happens client-side regardless.
 */
const NO_REDIRECT_ENDPOINTS = ['/api/auth/logout'];

/**
 * Handle API error with redirect
 */
function handleApiError(error: any, endpoint: string, suppressRedirect?: boolean) {
  const statusCode = error?.statusCode ? error?.statusCode : error;
  const errorMessage = error?.messageJp || error?.messageEn || error.message || t('E-COMMON-001', { ns: 'messages' });
  // Redirect to error page for critical status codes
  if (isCriticalStatusCode(statusCode)) {
    if (isCriticalHandlingInProgress) {
      throw new HandledRedirectError(statusCode);
    }

    isCriticalHandlingInProgress = true;
    cancelAllPendingRequests();

    if (!suppressRedirect && !NO_REDIRECT_ENDPOINTS.some((e) => endpoint.includes(e))) {
      navigateToSC014(statusCode, errorMessage);
      // Reset flag after SPA navigation so user can navigate back and trigger new critical error handling
      isCriticalHandlingInProgress = false;
      /**
       * Always navigate to SC014 with statusCode and message
       */
      function navigateToSC014(statusCode: number, errorMessage: string) {
        const state = {
          statusCode: statusCode || HttpStatusCode.NotFound,
          message: errorMessage || '',
        };
        if (navigationCallback) {
          navigationCallback(NAVIGATE_ROUTE.SC014, state);
        } else {
          // Fallback: use window.location.href (state will be lost)
          const params = new URLSearchParams({
            statusCode: String(statusCode || HttpStatusCode.NotFound),
          });
          globalThis.location.href = `${NAVIGATE_ROUTE.SC014}?${params.toString()}`;
        }
      }
    }
    throw new HandledRedirectError(statusCode);
  }

  // For 400 Bad Request and 409 Conflict, let component handle it (validation / conflict errors)
  if (statusCode === HttpStatusCode.BadRequest || statusCode === HttpStatusCode.Conflict) {
    throw error;
  }

  // For other errors, redirect to not found with error message
  if (navigationCallback) {
    navigationCallback(NAVIGATE_ROUTE.SC014, {
      statusCode: statusCode || 0,
      message: errorMessage,
    });
  }

  throw error;
}

/**
 * Base API call function with error handling
 */
export async function apiCall<T>(
  endpoint: string,
  options: Partial<AxiosRequestConfig> = {},
  suppressRedirect = false
): Promise<T> {
  const url = `${API_ENDPOINT}${endpoint}`;
  const abortController = options.signal ? null : new AbortController();
  if (abortController) {
    activeRequestControllers.add(abortController);
  }

  const config: AxiosRequestConfig = {
    ...options,
    url,
    method: options.method || 'GET',
    timeout: API_TIMEOUT,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    signal: options.signal ?? abortController?.signal,
  };

  // Axios automatically handles Content-Type for FormData
  if (options.data instanceof FormData) {
    delete (config.headers as Record<string, string>)['Content-Type'];
  }

  try {
    const response: any = await httpClient(config);
    const responseData: ApiResponse<T> = response;

    // Validate API response structure
    if (typeof responseData !== 'object' || responseData === null) {
      throw new Error('Invalid response format from server');
    }
    // Handle API success/failure flag
    if (!response.success) {
      const error = (response as ApiErrorMapping) ?? {};
      const errCode = response?.code || response?.errorCode || response?.httpStatus;
      const messageIds = ERROR_MESSAGE_MAP[errCode];
      Object.assign(error, {
        field: response?.field || null,
        code: response?.code || response?.errorCode || response?.httpStatus,
        messageJp: response?.messageJp || messageIds?.messageIdJP,
        messageEn: response?.messageEn || messageIds?.messageIdEN,
        statusCode: response?.httpStatus || response?.status,
      });
      handleApiError(error, endpoint, suppressRedirect);

      throw error;
    }

    return responseData as T;
  } catch (error: any) {
    if (isRequestCanceledError(error)) {
      throw new HandledRedirectError(0);
    }

    // If critical redirect was already handled, silently swallow duplicated errors
    if (error instanceof HandledRedirectError) {
      throw error;
    }

    // Handle timeout
    if (axios.isAxiosError(error) && error.code === CommonErrorType.ECONNABORTED) {
      const timeoutError = new Error('Request timeout');
      Object.assign(timeoutError, getErrorMessage(HttpStatusCode.RequestTimeout, error.response?.data));
      throw timeoutError;
    }

    // Handle HTTP error status codes
    if (axios.isAxiosError(error) && error.response) {
      const statusCode = error.response.status;
      const errorInfo = getErrorMessage(statusCode, error.response.data);

      const apiError: any = new Error(errorInfo.messageJp);

      Object.assign(apiError, errorInfo);
      if (typeof errorInfo.statusCode === 'number') {
        handleApiError(errorInfo.statusCode, endpoint, suppressRedirect);
      } else {
        handleApiError(apiError, endpoint, suppressRedirect);
      }
    }

    // Handle network errors
    if (axios.isAxiosError(error) && !error.response) {
      const networkError = new Error('Network connection error');
      Object.assign(networkError, {
        code: 'NETWORK_ERROR',
        messageJp: 'ネットワーク接続エラーが発生しました。インターネット接続を確認してください。',
        messageEn: 'A network connection error occurred. Please check your internet connection.',
        statusCode: 0,
      });
      throw networkError;
    }

    // Handle other errors
    throw error;
  } finally {
    if (abortController) {
      activeRequestControllers.delete(abortController);
    }
  }
}

/**
 * API call using APS (View Design) endpoint — uses apsHttpClient with VITE_APS_API_ENDPOINT as baseURL.
 */
export async function apsApiCall<T>(
  endpoint: string,
  options: Partial<AxiosRequestConfig> = {},
  suppressRedirect = false
): Promise<T> {
  const abortController = options.signal ? null : new AbortController();
  if (abortController) {
    activeRequestControllers.add(abortController);
  }

  const config: AxiosRequestConfig = {
    ...options,
    url: endpoint,
    method: options.method || 'GET',
    timeout: API_TIMEOUT,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    signal: options.signal ?? abortController?.signal,
  };

  if (options.data instanceof FormData) {
    delete (config.headers as Record<string, string>)['Content-Type'];
  }

  try {
    const response: any = await apsHttpClient(config);
    const responseData: ApiResponse<T> = response;

    if (typeof responseData !== 'object' || responseData === null) {
      throw new Error('Invalid response format from server');
    }
    if (!response.success) {
      const error = new Error(
        (response as any).messageJp || (response as any).messageEn || (response as any).errorMessage || 'Request failed'
      );
      const errCode = (response as any).code || (response as any).errorCode || (response as any).httpStatus;
      Object.assign(error, {
        code: errCode,
        messageJp: (response as any).messageJp,
        messageEn: (response as any).messageEn,
        statusCode: response.httpStatus || (response as any).status,
      });
      handleApiError(error, endpoint, suppressRedirect);

      throw error;
    }

    return responseData as T;
  } catch (error: any) {
    if (isRequestCanceledError(error)) {
      throw new HandledRedirectError(0);
    }

    // If critical redirect was already handled, silently swallow duplicated errors
    if (error instanceof HandledRedirectError) {
      throw error;
    }

    if (axios.isAxiosError(error) && error.code === CommonErrorType.ECONNABORTED) {
      const timeoutError = new Error('Request timeout');
      Object.assign(timeoutError, getErrorMessage(HttpStatusCode.RequestTimeout, error.response?.data));
      throw timeoutError;
    }
    if (axios.isAxiosError(error) && error.response) {
      const statusCode = error.response.status;
      const errorInfo = getErrorMessage(statusCode, error.response.data);
      const apiError: any = new Error(errorInfo.messageJp);
      Object.assign(apiError, errorInfo);
      if (typeof errorInfo.statusCode === 'number') {
        handleApiError(errorInfo.statusCode, endpoint, suppressRedirect);
      } else {
        handleApiError(apiError, endpoint, suppressRedirect);
      }
    }
    if (axios.isAxiosError(error) && !error.response) {
      const networkError = new Error('Network connection error');
      Object.assign(networkError, {
        code: 'NETWORK_ERROR',
        messageJp: 'ネットワーク接続エラーが発生しました。インターネット接続を確認してください。',
        messageEn: 'A network connection error occurred. Please check your internet connection.',
        statusCode: 0,
      });
      throw networkError;
    }
    throw error;
  } finally {
    if (abortController) {
      activeRequestControllers.delete(abortController);
    }
  }
}

/**
 * API call function with custom timeout
 */
export async function apiCallWithTimeout<T>(
  endpoint: string,
  timeoutMs: number,
  options: Partial<AxiosRequestConfig> = {}
): Promise<T> {
  const url = `${API_ENDPOINT}${endpoint}`;
  const abortController = options.signal ? null : new AbortController();
  if (abortController) {
    activeRequestControllers.add(abortController);
  }

  const config: AxiosRequestConfig = {
    ...options,
    url,
    method: options.method || 'GET',
    timeout: timeoutMs,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    signal: options.signal ?? abortController?.signal,
  };

  // Axios automatically handles Content-Type for FormData
  if (options.data instanceof FormData) {
    delete (config.headers as Record<string, string>)['Content-Type'];
  }

  try {
    const response: any = await httpClient(config);
    const responseData: ApiResponse<T> = response.data;

    // Validate API response structure
    if (typeof responseData !== 'object' || responseData === null) {
      throw new Error('Invalid response format from server');
    }

    // Handle API success/failure flag
    if (!response.success) {
      const error = new Error(responseData.messageJp || responseData.messageEn || 'Request failed');
      Object.assign(error, {
        code: responseData.code,
        messageJp: responseData.messageJp,
        messageEn: responseData.messageEn,
        statusCode: response.status,
      });
      handleApiError(error, endpoint);
      throw error;
    }

    return responseData as T;
  } catch (error: any) {
    if (isRequestCanceledError(error)) {
      throw new HandledRedirectError(0);
    }

    // If critical redirect was already handled, silently swallow duplicated errors
    if (error instanceof HandledRedirectError) {
      throw error;
    }

    if (axios.isAxiosError(error) && error.code === CommonErrorType.ECONNABORTED) {
      const timeoutError = new Error('Request timeout');
      Object.assign(timeoutError, getErrorMessage(HttpStatusCode.RequestTimeout, error.response?.data));
      throw timeoutError;
    }

    if (axios.isAxiosError(error) && error.response) {
      const statusCode = error.response.status;
      const errorInfo = getErrorMessage(statusCode, error.response.data);
      const apiError = new Error(errorInfo.messageJp);
      Object.assign(apiError, errorInfo);

      handleApiError(apiError, endpoint);
    }

    if (axios.isAxiosError(error) && !error.response) {
      const networkError = new Error('Network connection error');
      Object.assign(networkError, {
        code: 'NETWORK_ERROR',
        messageJp: 'ネットワーク接続エラーが発生しました。インターネット接続を確認してください。',
        messageEn: 'A network connection error occurred. Please check your internet connection.',
        statusCode: 0,
      });
      throw networkError;
    }

    throw error;
  } finally {
    if (abortController) {
      activeRequestControllers.delete(abortController);
    }
  }
}

/**
 * Download file helper function
 */
export async function downloadFile(
  url: string,
  method: string = 'GET',
  defaultFilename: string,
  payload?: any
): Promise<void> {
  try {
    const headers = new AxiosHeaders();
    updateAuthorizationHeader(headers);

    const response = await axios({
      url,
      method,
      data: method === 'POST' ? payload : undefined,
      responseType: 'blob',
      timeout: API_TIMEOUT,
      withCredentials: true,
      headers,
    });

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = '';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    if (!filename) {
      filename = defaultFilename;
    }

    // Download the file
    const blob = new Blob([response.data], {
      type: response.headers['content-type']?.toString(),
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    if (axios.isAxiosError(error) && error.code === CommonErrorType.ECONNABORTED) {
      throw new Error('Request timeout');
    }
    throw error;
  }
}

/**
 * API Service
 */
export const apiService = {
  getModels: (): Promise<BucketObject[]> => {
    return apiCall<BucketObject[]>('/api/models');
  },

  /**
   * Get list of models
   */
  getProjectFiles: (projectId: string): Promise<FileStorageRecord[]> => {
    return apiCall<FileStorageRecord[]>(`/api/models/storage/project/${projectId}`);
  },

  /**
   * Get models by plan IDs
   */
  getModelsByPlanIds: (planIds: string[]): Promise<BucketObject[]> => {
    const queryParams = planIds.map((id) => `planIds=${encodeURIComponent(id)}`).join('&');
    return apiCall<BucketObject[]>(`/api/models?${queryParams}`);
  },

  /**
   * Get model translation status
   */
  getModelStatus: (urn: string): Promise<TranslationStatus> => {
    return apiCall<TranslationStatus>(`/api/models/${urn}/status`);
  },

  /**
   * Upload and translate model
   */
  uploadModel: (formData: FormData): Promise<BucketObject> => {
    // Use 10 minutes timeout for model upload
    return apiCallWithTimeout<BucketObject>('/api/models', 600000, {
      method: 'POST',
      data: formData,
    });
  },

  /**
   * Get access token for Autodesk Viewer
   */
  getAccessToken: async (): Promise<AccessToken> => {
    const baseUrl = import.meta.env.VITE_API_ENDPOINT || '';
    const headers = new AxiosHeaders();
    updateAuthorizationHeader(headers);
    const response = await axios.post<AccessToken>(
      `${baseUrl}${AUTH_ENDPOINTS.ACCESS_TOKEN}`,
      {},
      {
        headers,
        withCredentials: true,
        timeout: API_TIMEOUT,
      }
    );
    return response.data;
  },

  /**
   * Download BCF file for a project
   */
  downloadBcf: async (endpoint: string, defaultFilename: string): Promise<void> => {
    const url = `${API_ENDPOINT}${endpoint}`;
    return downloadFile(url, 'GET', defaultFilename);
  },

  /**
   * Download Excel file for topics
   */
  downloadExcel: async (endpoint: string, defaultFilename: string): Promise<void> => {
    const url = `${API_ENDPOINT}${endpoint}`;
    return downloadFile(url, 'GET', defaultFilename);
  },

  /**
   * Get all stored files for a building
   * GET /api/models/storage/building/{buildingId}
   */
  getBuildingFiles: (buildingId: string): Promise<FileStorageRecord[]> => {
    return apiCall<FileStorageRecord[]>(`/api/models/storage/building/${buildingId}`);
  },

  /**
   * Download file from storage by URN and version
   * GET /api/models/storage/download/{urn}/{version}
   */
};
